import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  X,
  ArrowRight,
  Clock,
  CornerDownLeft,
  Gamepad2,
  BookOpen,
  Users,
  GraduationCap,
  FileText,
} from "lucide-react";
import { SEARCHABLE_PAGES } from "../data/searchablePages";
import { useBslSigns } from "../lib/useBslSigns";

/* Site-wide instant search. Opens via the header button (or `/` key)
   into a centered overlay panel. Fuse.js powers fuzzy matching over:
   - The static page index (`searchablePages.js`)
   - Every BSL sign in the live Dictionary feed
   Keyboard: Up/Down navigate, Enter open, Esc close. Recent searches
   are remembered per-browser. */

const RECENT_KEY = "bsl-search-recent-v1";
const MAX_RESULTS = 12;

const slugifyDict = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch (e) {
    return [];
  }
};
const writeRecent = (q) => {
  if (!q || q.length < 2) return;
  try {
    const cur = readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase());
    const next = [q, ...cur].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (e) {
    /* ignore */
  }
};

const TYPE_ICONS = {
  "Free activity": Gamepad2,
  "Lesson plan": BookOpen,
  Audience: Users,
  Course: GraduationCap,
  Page: FileText,
  Dictionary: BookOpen,
};

const SiteSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const { signs, status } = useBslSigns();

  // fuse.js is only needed once the visitor actually searches, so load it
  // lazily when the overlay first opens rather than shipping it in the main
  // bundle (the header search is on every page but rarely used immediately).
  const [FuseCtor, setFuseCtor] = useState(null);
  useEffect(() => {
    if (open && !FuseCtor) {
      import("fuse.js").then((m) => setFuseCtor(() => m.default));
    }
  }, [open, FuseCtor]);

  /* Build the combined index when the overlay is first opened and
     whenever the dictionary feed flips from loading -> ready. */
  const documents = useMemo(() => {
    const dict = signs.map((s) => ({
      type: "Dictionary",
      title: s.name,
      href: `/bsl-dictionary/${slugifyDict(s.name)}`,
      body: `BSL sign for ${s.name}.`,
      imageUrl: s.image_url,
    }));
    return [...SEARCHABLE_PAGES, ...dict];
  }, [signs]);

  const fuse = useMemo(
    () =>
      FuseCtor
        ? new FuseCtor(documents, {
            keys: [
              { name: "title", weight: 0.7 },
              { name: "body", weight: 0.2 },
              { name: "type", weight: 0.1 },
            ],
            threshold: 0.34,
            ignoreLocation: true,
            minMatchCharLength: 2,
          })
        : null,
    [documents, FuseCtor]
  );

  const results = useMemo(() => {
    if (!query.trim() || !fuse) return [];
    return fuse.search(query.trim()).slice(0, MAX_RESULTS).map((r) => r.item);
  }, [query, fuse]);

  /* Group results by type for a clearer overlay layout. */
  const grouped = useMemo(() => {
    const order = ["Free activity", "Lesson plan", "Audience", "Course", "Page", "Dictionary"];
    const map = new Map();
    results.forEach((r) => {
      if (!map.has(r.type)) map.set(r.type, []);
      map.get(r.type).push(r);
    });
    return order
      .filter((k) => map.has(k))
      .map((k) => ({ type: k, items: map.get(k) }));
  }, [results]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !open && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        openPanel();
      } else if (e.key === "Escape" && open) {
        closePanel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  /* Bootstrap the overlay from a ?q= URL parameter. Lets the
     Schema.org SearchAction in index.html route SERP sitelinks-search
     queries straight into the live search UI. Runs once on mount. */
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("q");
      if (q && q.trim()) {
        setQuery(q.trim());
        openPanel();
      }
    } catch (e) {
      /* ignore – non-browser env or malformed URL */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPanel = () => {
    setRecent(readRecent());
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  };
  const closePanel = () => {
    setOpen(false);
    setQuery("");
    setActive(0);
  };

  /* Lock body scroll while the overlay is open. */
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const openItem = (item) => {
    if (!item) return;
    writeRecent(query);
    closePanel();
    window.location.assign(item.href);
  };

  /* Flat result list for keyboard navigation. */
  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[active]) openItem(flat[active]);
    }
  };

  return (
    <>
      <button
        type="button"
        className="bs-search-trigger"
        onClick={openPanel}
        aria-label="Search the site (press / to open)"
        title="Search"
        data-testid="site-search-trigger"
      >
        <Search size={18} aria-hidden="true" />
      </button>

      {open && (
        <div
          className="bs-search-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Site search"
          onMouseDown={(e) => {
            if (e.target.classList.contains("bs-search-overlay")) closePanel();
          }}
          data-testid="site-search-overlay"
        >
          <div className="bs-search-panel">
            <div className="bs-search-input-row">
              <Search size={18} aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                className="bs-search-input"
                placeholder="Search signs, activities, lesson plans..."
                value={query}
                onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                onKeyDown={onInputKey}
                data-testid="site-search-input"
              />
              <button
                type="button"
                className="bs-search-close"
                onClick={closePanel}
                aria-label="Close search"
                data-testid="site-search-close"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            <div className="bs-search-body">
              {!query.trim() && recent.length > 0 && (
                <div className="bs-search-section">
                  <h4 className="bs-search-section-title">
                    <Clock size={11} aria-hidden="true" /> Recent
                  </h4>
                  <div className="bs-search-recents">
                    {recent.map((r) => (
                      <button
                        key={r}
                        type="button"
                        className="bs-search-recent"
                        onClick={() => setQuery(r)}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!query.trim() && (
                <div className="bs-search-empty">
                  <p>Try {"\u201c"}<strong>hello</strong>{"\u201d"}, {"\u201c"}<strong>lesson plan</strong>{"\u201d"}, {"\u201c"}<strong>word search</strong>{"\u201d"} or {"\u201c"}<strong>scouts</strong>{"\u201d"}.</p>
                  <p className="bs-search-hint">
                    Tip: press <kbd>/</kbd> from anywhere on the site to open search, <kbd>Esc</kbd> to close.
                  </p>
                </div>
              )}

              {query.trim() && results.length === 0 && status === "ready" && (
                <div className="bs-search-empty">
                  <p>No matches for {"\u201c"}<strong>{query}</strong>{"\u201d"}.</p>
                  <p className="bs-search-hint">Try a shorter or more general term.</p>
                </div>
              )}

              {grouped.map((g) => (
                <div key={g.type} className="bs-search-section">
                  <h4 className="bs-search-section-title">{g.type}</h4>
                  <ul className="bs-search-results">
                    {g.items.map((item) => {
                      const idx = flat.indexOf(item);
                      const isActive = idx === active;
                      return (
                        <li key={item.href}>
                          <button
                            type="button"
                            className={`bs-search-result ${isActive ? "is-active" : ""}`}
                            onClick={() => openItem(item)}
                            onMouseEnter={() => setActive(idx)}
                            data-testid={`site-search-result-${slugifyDict(item.title)}`}
                          >
                            {item.imageUrl ? (
                              <span className="bs-search-result-thumb">
                                <img
                                  src={item.imageUrl}
                                  alt=""
                                  loading="lazy"
                                />
                              </span>
                            ) : (
                              <span className="bs-search-result-thumb bs-search-result-thumb--type">
                                {(() => {
                                  const Icon = TYPE_ICONS[item.type] || FileText;
                                  return <Icon size={18} aria-hidden="true" strokeWidth={1.75} />;
                                })()}
                              </span>
                            )}
                            <span className="bs-search-result-body">
                              <span className="bs-search-result-title">{item.title}</span>
                              <span className="bs-search-result-body-line">{item.body}</span>
                            </span>
                            <ArrowRight size={14} aria-hidden="true" className="bs-search-result-arrow" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <footer className="bs-search-footer">
              <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
              <span><kbd><CornerDownLeft size={11} /></kbd> Open</span>
              <span><kbd>Esc</kbd> Close</span>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default SiteSearch;
